import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';

// Dynamically import react-select with SSR disabled to prevent hydration mismatch
const Select = dynamic(() => import('react-select'), { ssr: false });

export default function TypeaheadInput({
  options,
  onChange,
  onInputChange,
  isClearable,
  placeholder,
  styles,
  className,
  filterMode = false,
  value: externalValue,
  ...props
}) {
  let classNames = className ? className : 'my-5';
  let selectStyles = styles
    ? styles
    : {
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderWidth: '0',
          boxShadow: 'none',
          borderRadius: '10px',
          height: '50px',
        }),
        menu: (styles, { baseStyles, state }) => ({
          ...styles,
          borderWidth: '0',
          borderRadius: '10px',
          backgroundColor: 'white',
        }),
        menuList: (styles, { baseStyles, state }) => ({
          ...styles,
          borderWidth: '0',
          borderRadius: '10px',
        }),
        valueContainer: (styles, { baseStyles, state }) => ({
          ...styles,
          paddingLeft: '10px',
        }),
        singleValue: (styles, { baseStyles, state }) => ({
          ...styles,
          fontSize: '15px',
          paddingLeft: '0',
        }),
        option: (styles, { isFocused }) => ({
          borderWidth: '0',
          padding: '12px 16px 10px',
          fontSize: '14px',
          backgroundColor: isFocused ? '#fff5ea' : 'inherit',
          ':hover': {
            backgroundColor: '#fff5ea',
            cursor: 'pointer',
          },
        }),
      };

  // Hooks must be called at the top level, not conditionally
  const [inputValue, setInputValue] = useState('');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Update local state when external value changes (e.g., when filters are reset)
  useEffect(() => {
    if (externalValue !== undefined) {
      setInputValue(externalValue);
    }
  }, [externalValue]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    onInputChange && onInputChange(value);
  };

  const handleClear = () => {
    setInputValue('');
    onInputChange && onInputChange('');
  };

  // If filterMode is true, hide the dropdown and use as search input
  if (filterMode) {
    return (
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          placeholder={placeholder}
          className={`w-full h-[50px] px-4 py-3 border-2 border-beige-100 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brown-400 ${classNames}`}
          onChange={handleInputChange}
          aria-label={placeholder || 'Search input'}
        />
        {isClearable && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Clear search"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  // Show placeholder input until Select is loaded
  if (!isMounted) {
    return (
      <div className={classNames}>
        <div className="h-[50px] rounded-[10px] bg-white flex items-center px-3 text-gray-400 text-sm">
          {placeholder}
        </div>
      </div>
    );
  }

  return (
    <Select
      options={options}
      onChange={onChange}
      defaultOptions
      isClearable={true}
      placeholder={placeholder}
      {...props}
      className={classNames}
      styles={selectStyles}
      aria-label={placeholder || 'Input'}
      instanceId={
        placeholder
          ? `select-${placeholder.replace(/\s+/g, '-').toLowerCase()}`
          : 'select-input'
      }
    />
  );
}
